/* eslint-disable custom-rules/data-testid-required */
import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Flex,
  Grid,
  GridItem,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Select,
  useDisclosure,
  Divider,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@heroicons/react/outline";
import propTypes from "prop-types";
import { DateTime } from "luxon";

import {
  appliedDateFormat,
  appliedTimeFormat,
  shorthandOperators,
  timeRanges,
} from "../DateTimeRange/DateTimeDefault";
import useDateTimeRange from "../../hooks/useDateTimeRange";

const granularityOptions = [
  "Automatic",
  "Thirty Minutes",
  "Hour",
  "Six Hours",
  "Twelve Hours",
  "Day",
  "Week",
  "Month",
  "Quarter",
];

const DateTimeGranularity = ({
  defaultValue = {
    granularity: granularityOptions[0],
    timeRange: timeRanges[0].value,
  },
  id,
  isDisabled,
  name,
  onChange,
  size,
  value,
  variant,
  ...rest
}) => {
  // State Hooks
  const [shorthandValue, setShorthandValue] = useState(defaultValue?.timeRange);
  const [granularityValue, setGranularityValue] = useState(
    defaultValue?.granularity
  );
  const [appliedValues, setAppliedValues] = useState(
    defaultValue ??
      value ?? {
        ...shorthandOperators[defaultValue.timeRange](
          appliedDateFormat,
          appliedTimeFormat
        ),
        granularity: "Automatic",
      }
  );

  // Utility Hooks
  const { setValue, setValues, error, isError, ...dateTimeValues } =
    useDateTimeRange({
      dateFormat: appliedDateFormat,
      shorthandOperators,
      timeFormat: appliedTimeFormat,
    });

  const initialFocusRef = useRef();
  const { isOpen, onClose, onOpen } = useDisclosure();

  // Keeps the component in a controlled state if there is a "value" prop present
  useEffect(() => {
    if (value) {
      const { granularity, timeRange, ...rest } = value;
      setShorthandValue(timeRange);
      setGranularityValue(granularity);
      setValues(rest);
      setAppliedValues(value);
    }
  }, [value]);

  // Event Handlers
  const handleDateTimeChange = (eventValue, key) => {
    setValue(key, eventValue);
    setShorthandValue("custom");
  };

  const handleDateTimeOptionChange = (eventValue) => {
    if (Object.keys(shorthandOperators).includes(eventValue)) {
      setValue(eventValue);
    }

    setShorthandValue(eventValue);
  };

  const handleApply = () => {
    const compiledValues = {
      ...dateTimeValues,
      granularity: granularityValue,
      timeRange: shorthandValue,
    };

    onClose();
    setAppliedValues(compiledValues);

    if (typeof onChange === "function") {
      onChange(compiledValues);
    }
  };

  const handleGranularityChange = (event) => {
    setGranularityValue(event.target.value);
  };

  const handleCloseWithoutApply = () => {
    onClose();
    setShorthandValue(appliedValues.timeRange);
    setGranularityValue(appliedValues.granularity);

    setValues({
      endDate: appliedValues.endDate,
      endTime: appliedValues.endTime,
      startDate: appliedValues.startDate,
      startTime: appliedValues.startTime,
    });
  };

  const handleOpen = () => {
    handleDateTimeOptionChange(appliedValues.timeRange);
    onOpen();
  };

  const formatDisplayValue = () => {
    if (appliedValues.timeRange === "custom") {
      const endDateTime = DateTime.fromFormat(
        `${appliedValues.endDate} ${appliedValues.endTime}`,
        `${appliedDateFormat} ${appliedTimeFormat}`
      );

      const startDateTime = DateTime.fromFormat(
        `${appliedValues.startDate} ${appliedValues.startTime}`,
        `${appliedDateFormat} ${appliedTimeFormat}`
      );

      return `${startDateTime.toFormat("f")} - ${endDateTime.toFormat("f")}`;
    } else {
      return timeRanges.find((t) => t.value === appliedValues.timeRange).label;
    }
  };

  return (
    <Popover
      computePositionOnMount
      initialFocusRef={initialFocusRef}
      isOpen={isOpen}
      onClose={handleCloseWithoutApply}
      placement="bottom-end"
    >
      <PopoverTrigger>
        <InputGroup onClick={!isDisabled ? handleOpen : null} {...rest}>
          <Input
            cursor={isDisabled ? "not-allowed" : "pointer"}
            id={id}
            isDisabled={isDisabled}
            isReadOnly
            name={name}
            size={size}
            value={formatDisplayValue()}
            variant={variant}
          />

          <InputRightElement>
            <IconButton
              isDisabled={isDisabled}
              icon={<Icon as={ChevronDownIcon} />}
              variant="unstyled"
            />
          </InputRightElement>
        </InputGroup>
      </PopoverTrigger>

      <PopoverContent width="40rem">
        <PopoverArrow />

        <PopoverHeader>
          Select Time Range
          <PopoverCloseButton />
        </PopoverHeader>

        <PopoverBody>
          <Grid gap="2" templateColumns="2fr min-content 1fr">
            <Grid gap="2" templateColumns="1fr 1fr">
              <GridItem colSpan={2}>
                {isError && (
                  <Alert status="error">
                    <AlertIcon />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <FormControl>
                  <FormLabel>Time Range</FormLabel>

                  <Select
                    onChange={(e) => handleDateTimeOptionChange(e.target.value)}
                    ref={initialFocusRef}
                    value={shorthandValue}
                  >
                    {timeRanges.map((timeRange) => (
                      <option key={timeRange.value} value={timeRange.value}>
                        {timeRange.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>

              <FormControl isInvalid={!dateTimeValues.startDate}>
                <FormLabel>Start Date</FormLabel>

                <Input
                  max={dateTimeValues.endDate}
                  onChange={(e) =>
                    handleDateTimeChange(e.target.value, "startDate")
                  }
                  type="date"
                  value={dateTimeValues.startDate}
                />

                <FormErrorMessage>Invalid Value</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!dateTimeValues.endDate}>
                <FormLabel>End Date</FormLabel>

                <Input
                  min={dateTimeValues.startDate}
                  onChange={(e) =>
                    handleDateTimeChange(e.target.value, "endDate")
                  }
                  type="date"
                  value={dateTimeValues.endDate}
                />

                <FormErrorMessage>Invalid Value</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!dateTimeValues.startTime}>
                <FormLabel>Start Time</FormLabel>

                <Input
                  onChange={(e) =>
                    handleDateTimeChange(e.target.value, "startTime")
                  }
                  type="time"
                  value={dateTimeValues.startTime}
                />

                <FormErrorMessage>Invalid Value</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!dateTimeValues.endTime}>
                <FormLabel>End Time</FormLabel>

                <Input
                  onChange={(e) =>
                    handleDateTimeChange(e.target.value, "endTime")
                  }
                  type="time"
                  value={dateTimeValues.endTime}
                />

                <FormErrorMessage>Invalid Value</FormErrorMessage>
              </FormControl>
            </Grid>

            <Divider orientation="vertical" />

            <FormControl>
              <FormLabel>Granularity</FormLabel>

              <Select
                onChange={handleGranularityChange}
                value={granularityValue}
              >
                {granularityOptions.map((option) => (
                  <option key={option} value={option.replace(" ", "")}>
                    {option}
                  </option>
                ))}
              </Select>
            </FormControl>

            <GridItem colSpan={3}>
              <Divider orientation="horizontal" />
            </GridItem>

            <GridItem as={Flex} justify="flex-end" colSpan={3}>
              <Button
                isDisabled={isError}
                onClick={handleApply}
                variant="primary"
              >
                Apply
              </Button>
            </GridItem>
          </Grid>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

DateTimeGranularity.displayName = "DateTimeRange";

DateTimeGranularity.propTypes = {
  defaultValue: propTypes.shape({
    endDate: propTypes.string,
    endTime: propTypes.string,
    granularity: propTypes.string,
    startDate: propTypes.string,
    startTime: propTypes.string,
    timeRange: propTypes.string,
  }),
  id: propTypes.string,
  isDisabled: propTypes.bool,
  name: propTypes.string,
  onChange: propTypes.func,
  size: propTypes.string,
  value: propTypes.shape({
    endDate: propTypes.string,
    endTime: propTypes.string,
    granularity: propTypes.string,
    startDate: propTypes.string,
    startTime: propTypes.string,
    timeRange: propTypes.string,
  }),
  variant: propTypes.string,
};

export default DateTimeGranularity;
